import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Avatar, Badge, Box, Button, Card, Center, Heading, HStack, Icon, Input, InputGroup, Separator, SimpleGrid, Spinner, Text, VStack, Wrap } from '@chakra-ui/react';
import { Switch } from '@src/components/chakra-ui/switch';
import { ADVISORS_DIRECTORY_PAGE_SIZE } from '@src/components/sco/Advisors/constants';
import { DIR_CATEGORIES } from '@src/components/sco/Advisors/data/dirCategories';
import { useAdvisorsCollectionLoadMore } from '@src/components/sco/Advisors/hooks/useAdvisorsCollectionLoadMore';
import { advisorsBreadcrumbsWithTrail, useAdvisorsToolbar } from '@src/components/sco/Advisors/hooks/useAdvisorsToolbar';
import { useBoardSeatCounts } from '@src/components/sco/Advisors/hooks/useBoardSeatCounts';
import { useDirectoryNamedSeatMatches } from '@src/components/sco/Advisors/hooks/useDirectoryNamedSeatMatches';
import { useIsMobile } from '@src/components/sco/Advisors/hooks/useIsMobile';
import { InviteToBoardModal } from '@src/components/sco/Advisors/modals/InviteToBoardModal';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { AdvisorMarketplaceActionButton } from '@src/components/sco/Advisors/shared/AdvisorMarketplaceActionButton';
import { AdvisorsLoadMore } from '@src/components/sco/Advisors/shared/AdvisorsLoadMore';
import { SpecialtyTag } from '@src/components/sco/Advisors/shared/SpecialtyTag';
import { advisorMarketplaceActions } from '@src/components/sco/Advisors/utils/advisorMarketplaceActions';
import { CATEGORY_KEY_TO_NAME } from '@src/components/sco/Advisors/utils/categoryKeys';
import { ACCEPTING_SESSIONS_QUERY_KEY, parseAcceptingSessionsFilter } from '@src/components/sco/Advisors/utils/directoryFilters';
import { NestedAppLayout, attachedNavPanelContainerProps, attachedPanelContainerProps } from '@src/components/sco/AppLayout';
import { AppPanelToggleButton } from '@src/components/sco/AppLayout/Panel/AppPanelToggleButton';
import { SidebarNestedNavList, SidebarNestedNavListSection } from '@src/components/sco/SidebarNestedNavList';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { ADVISOR_DIRECTORY } from '@src/gql/queries/AdvisorDirectory';
import { ScoAdvisorProfile } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { LuLayoutGrid, LuSearch, LuSparkles, LuX } from 'react-icons/lu';

/**
 * Find advisors — split panel: filters (left) + results grid (wider right).
 * Category discovery also lives on `/advisors/browse`.
 */
export const DirectorySection = () => {
  const $ = _c(161);
  const paths = useAdvisorsPaths();
  const isMobile = useIsMobile();
  const router = useRouter();
  const [keywords, setKeywords] = useState("");
  const [searchKeywords, setSearchKeywords] = useState(undefined);
  const [inviteUserId, setInviteUserId] = useState(null);
  const {
    byTargetUserId: namedSeatMatches,
    refetch: refetchSeatMatches
  } = useDirectoryNamedSeatMatches();
  const {
    counts: seatCounts
  } = useBoardSeatCounts();
  const hasOpenSeats = seatCounts.drafts + seatCounts.searching > 0;
  const {
    currentUser
  } = useCurrentUser();
  let t0;
  if ($[0] !== paths.directory) {
    t0 = advisorsBreadcrumbsWithTrail({
      title: "Find advisors",
      href: paths.directory
    });
    $[0] = paths.directory;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = {
      breadcrumbs: t0
    };
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  useAdvisorsToolbar(t1);
  const categoryKeyFromRoute = typeof router.query.categoryKey === "string" ? router.query.categoryKey : typeof router.query.category === "string" ? router.query.category : undefined;
  const t2 = router.query[ACCEPTING_SESSIONS_QUERY_KEY];
  let t3;
  if ($[4] !== t2) {
    t3 = parseAcceptingSessionsFilter(t2);
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const acceptingSessions = t3;
  const t4 = acceptingSessions || undefined;
  let t5;
  if ($[6] !== categoryKeyFromRoute || $[7] !== searchKeywords || $[8] !== t4) {
    t5 = {
      categoryKey: categoryKeyFromRoute,
      keywords: searchKeywords,
      acceptingSessions: t4,
      page: 1,
      pageSize: ADVISORS_DIRECTORY_PAGE_SIZE
    };
    $[6] = categoryKeyFromRoute;
    $[7] = searchKeywords;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = !router.isReady;
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = {
      variables: t5,
      fetchPolicy: "network-only",
      skip: t6
    };
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const query = useQuery(ADVISOR_DIRECTORY, t7);
  let t8;
  if ($[13] !== query.data?.advisorDirectory?.items) {
    t8 = query.data?.advisorDirectory?.items ?? [];
    $[13] = query.data?.advisorDirectory?.items;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t8) {
    t9 = t8.filter(_temp);
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const firstPageAdvisors = t9;
  const t10 = query.fetchMore;
  const t11 = query.data?.advisorDirectory?.pagination?.hasNextPage;
  const t12 = acceptingSessions ? "1" : "0";
  let t13;
  if ($[17] !== categoryKeyFromRoute || $[18] !== searchKeywords || $[19] !== t12) {
    t13 = [categoryKeyFromRoute, searchKeywords, t12];
    $[17] = categoryKeyFromRoute;
    $[18] = searchKeywords;
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const t14 = t13.join("|");
  let t15;
  if ($[21] !== firstPageAdvisors || $[22] !== query.fetchMore || $[23] !== t11 || $[24] !== t14) {
    t15 = {
      fetchMore: t10,
      field: "advisorDirectory",
      firstPageItems: firstPageAdvisors,
      hasNextPage: t11,
      resetKey: t14
    };
    $[21] = firstPageAdvisors;
    $[22] = query.fetchMore;
    $[23] = t11;
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  const {
    items: advisors,
    loadMore,
    loadingMore,
    hasNextPage
  } = useAdvisorsCollectionLoadMore(t15);
  let t16;
  let t17;
  if ($[26] !== router.isReady || $[27] !== router.query.q) {
    t16 = () => {
      if (!router.isReady) {
        return;
      }
      if (typeof router.query.q === "string") {
        setKeywords(router.query.q);
        setSearchKeywords(router.query.q);
      } else {
        setKeywords("");
        setSearchKeywords(undefined);
      }
    };
    t17 = [router.isReady, router.query.q];
    $[26] = router.isReady;
    $[27] = router.query.q;
    $[28] = t16;
    $[29] = t17;
  } else {
    t16 = $[28];
    t17 = $[29];
  }
  useEffect(t16, t17);
  const itemCount = query.data?.advisorDirectory?.pagination?.itemCount ?? advisors.length;
  const initialLoad = query.loading && !query.data;
  let t18;
  if ($[30] !== acceptingSessions || $[31] !== categoryKeyFromRoute || $[32] !== paths || $[33] !== router || $[34] !== searchKeywords) {
    t18 = next => {
      const category = next.category === null ? undefined : next.category ?? categoryKeyFromRoute ?? undefined;
      const q = next.q === null ? undefined : next.q ?? searchKeywords ?? undefined;
      const sessionsOn = next.acceptingSessions === null ? false : next.acceptingSessions === undefined ? acceptingSessions : next.acceptingSessions;
      router.replace(paths.withQuery(paths.directory, {
        category,
        q,
        [ACCEPTING_SESSIONS_QUERY_KEY]: sessionsOn ? "1" : undefined
      }), undefined, {
        shallow: true
      });
    };
    $[30] = acceptingSessions;
    $[31] = categoryKeyFromRoute;
    $[32] = paths;
    $[33] = router;
    $[34] = searchKeywords;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  const replaceDirectoryQuery = t18;
  let t19;
  if ($[36] !== keywords || $[37] !== replaceDirectoryQuery) {
    t19 = () => {
      const trimmed = keywords.trim();
      setSearchKeywords(trimmed || undefined);
      replaceDirectoryQuery({
        q: trimmed || null
      });
    };
    $[36] = keywords;
    $[37] = replaceDirectoryQuery;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  const handleSearch = t19;
  let t20;
  if ($[39] !== replaceDirectoryQuery) {
    t20 = () => {
      setKeywords("");
      setSearchKeywords(undefined);
      replaceDirectoryQuery({
        category: null,
        q: null,
        acceptingSessions: null
      });
    };
    $[39] = replaceDirectoryQuery;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  const clearAllFilters = t20;
  const activeCategoryTitle = categoryKeyFromRoute ? CATEGORY_KEY_TO_NAME[categoryKeyFromRoute] || categoryKeyFromRoute : null;
  const hasActiveFilters = !!(categoryKeyFromRoute || searchKeywords || acceptingSessions);
  const q_0 = searchKeywords;
  const sessions = acceptingSessions ? "1" : undefined;
  let t21;
  if ($[41] !== paths || $[42] !== q_0 || $[43] !== sessions) {
    t21 = paths.withQuery(paths.directory, {
      q: q_0,
      [ACCEPTING_SESSIONS_QUERY_KEY]: sessions
    });
    $[41] = paths;
    $[42] = q_0;
    $[43] = sessions;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  const t22 = !categoryKeyFromRoute;
  let t23;
  if ($[45] !== t21 || $[46] !== t22) {
    t23 = {
      title: "All topics",
      href: t21,
      isActive: t22,
      activeExact: true
    };
    $[45] = t21;
    $[46] = t22;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== categoryKeyFromRoute || $[49] !== paths || $[50] !== q_0 || $[51] !== sessions || $[52] !== t23) {
    let t25;
    if ($[54] !== categoryKeyFromRoute || $[55] !== paths || $[56] !== q_0 || $[57] !== sessions) {
      t25 = cat => ({
        title: cat.name,
        href: paths.withQuery(paths.directory, {
          category: cat.key,
          q: q_0,
          [ACCEPTING_SESSIONS_QUERY_KEY]: sessions
        }),
        icon: cat.icon,
        isActive: categoryKeyFromRoute === cat.key,
        buttonProps: {
          _hover: {
            bg: "colorPalette.emphasized/20"
          }
        }
      });
      $[54] = categoryKeyFromRoute;
      $[55] = paths;
      $[56] = q_0;
      $[57] = sessions;
      $[58] = t25;
    } else {
      t25 = $[58];
    }
    t24 = [t23, ...DIR_CATEGORIES.map(t25)];
    $[48] = categoryKeyFromRoute;
    $[49] = paths;
    $[50] = q_0;
    $[51] = sessions;
    $[52] = t23;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  const topicSections = t24;
  const resultsLabel = acceptingSessions ? activeCategoryTitle ? `Accepting sessions in ${activeCategoryTitle}` : searchKeywords ? `Accepting sessions for “${searchKeywords}”` : "Advisors accepting booked sessions" : activeCategoryTitle ? `Advisors in ${activeCategoryTitle}` : searchKeywords ? `Results for “${searchKeywords}”` : "Approved advisors";
  let t25;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Text color="colorPalette.fg/60" textStyle="labelXs">Keywords</Text>;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Icon boxSize={4} color="colorPalette.solid"><LuSearch /></Icon>;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = event => setKeywords(event.currentTarget.value);
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== handleSearch) {
    t28 = event_0 => {
      if (event_0.key === "Enter") {
        handleSearch();
      }
    };
    $[62] = handleSearch;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== keywords || $[65] !== t28) {
    t29 = <InputGroup startElement={t26}><Input fontSize="sm" height="auto" paddingY={2.5} placeholder={"Name, headline, keyword\u2026"} value={keywords} onChange={t27} onKeyDown={t28} /></InputGroup>;
    $[64] = keywords;
    $[65] = t28;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== handleSearch) {
    t30 = <Button size="sm" variant="cta" width="full" onClick={handleSearch}>Search</Button>;
    $[67] = handleSearch;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== t29 || $[70] !== t30) {
    t31 = <VStack align="stretch" gap={2}>{t25}{t29}{t30}</VStack>;
    $[69] = t29;
    $[70] = t30;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  let t32;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <Separator />;
    $[72] = t32;
  } else {
    t32 = $[72];
  }
  let t33;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <VStack align="stretch" gap={0.5} minWidth={0}><Text fontSize="sm" fontWeight="medium">Accepting booked sessions</Text><Text color="colorPalette.fg/60" fontSize="xs" lineHeight={1.4}>Only advisors who take paid or free video bookings.</Text></VStack>;
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  let t34;
  if ($[74] !== replaceDirectoryQuery) {
    t34 = details => replaceDirectoryQuery({
      acceptingSessions: !!details.checked
    });
    $[74] = replaceDirectoryQuery;
    $[75] = t34;
  } else {
    t34 = $[75];
  }
  let t35;
  if ($[76] !== acceptingSessions || $[77] !== t34) {
    t35 = <HStack align="start" gap={3} justify="space-between">{t33}<Switch aria-label="Accepting booked sessions" checked={acceptingSessions} colorPalette="teal" onCheckedChange={t34} /></HStack>;
    $[76] = acceptingSessions;
    $[77] = t34;
    $[78] = t35;
  } else {
    t35 = $[78];
  }
  let t36;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <Separator />;
    $[79] = t36;
  } else {
    t36 = $[79];
  }
  let t37;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <Text color="colorPalette.fg/60" textStyle="labelXs">Topics</Text>;
    $[80] = t37;
  } else {
    t37 = $[80];
  }
  let t38;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <Icon boxSize={3.5}><LuLayoutGrid /></Icon>;
    $[81] = t38;
  } else {
    t38 = $[81];
  }
  let t39;
  if ($[82] !== paths.browse) {
    t39 = <HStack align="center" gap={2} justify="space-between" wrap="wrap">{t37}<Button asChild={true} size="xs" variant="ghost"><NextLink href={paths.browse}>{t38}Browse</NextLink></Button></HStack>;
    $[82] = paths.browse;
    $[83] = t39;
  } else {
    t39 = $[83];
  }
  let t40;
  if ($[84] !== topicSections) {
    t40 = <Box marginX={-3}><SidebarNestedNavList sections={topicSections} /></Box>;
    $[84] = topicSections;
    $[85] = t40;
  } else {
    t40 = $[85];
  }
  let t41;
  if ($[86] !== t39 || $[87] !== t40) {
    t41 = <VStack align="stretch" gap={2}>{t39}{t40}</VStack>;
    $[86] = t39;
    $[87] = t40;
    $[88] = t41;
  } else {
    t41 = $[88];
  }
  let t42;
  if ($[89] !== acceptingSessions || $[90] !== activeCategoryTitle || $[91] !== clearAllFilters || $[92] !== hasActiveFilters || $[93] !== searchKeywords) {
    t42 = hasActiveFilters && <><Separator /><VStack align="stretch" gap={2}>{activeCategoryTitle && <HStack color="colorPalette.fg/70" fontSize="xs" gap={1.5} wrap="wrap"><Text>Topic:{" "}<Text as="span" fontWeight="semibold">{activeCategoryTitle}</Text></Text></HStack>}{searchKeywords && <HStack color="colorPalette.fg/70" fontSize="xs" gap={1.5} wrap="wrap"><Text>Search:{" "}<Text as="span" fontWeight="semibold">“{searchKeywords}”</Text></Text></HStack>}{acceptingSessions && <HStack color="colorPalette.fg/70" fontSize="xs" gap={1.5} wrap="wrap"><Text>Filter:{" "}<Text as="span" fontWeight="semibold">Accepting booked sessions</Text></Text></HStack>}<Button size="xs" variant="outline" width="full" onClick={clearAllFilters}><Icon boxSize={3.5}><LuX /></Icon>Clear filters</Button></VStack></>;
    $[89] = acceptingSessions;
    $[90] = activeCategoryTitle;
    $[91] = clearAllFilters;
    $[92] = hasActiveFilters;
    $[93] = searchKeywords;
    $[94] = t42;
  } else {
    t42 = $[94];
  }
  let t43;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <Box bg="colorPalette.solid/5" borderRadius="md" padding={3}><HStack align="start" color="colorPalette.fg/60" gap={2}><Icon boxSize={3.5} flexShrink={0} marginTop={0.5}><LuSparkles /></Icon><Text fontSize="xs" lineHeight={1.4}>Match % appears only when Member Match has scored this person for one of your open seats — never a fake rank.</Text></HStack></Box>;
    $[95] = t43;
  } else {
    t43 = $[95];
  }
  let t44;
  if ($[96] !== t31 || $[97] !== t35 || $[98] !== t41 || $[99] !== t42) {
    t44 = <VStack align="stretch" gap={5} width="full">{t31}{t32}{t35}{t36}{t41}{t42}{t43}</VStack>;
    $[96] = t31;
    $[97] = t35;
    $[98] = t41;
    $[99] = t42;
    $[100] = t44;
  } else {
    t44 = $[100];
  }
  const filterPanel = t44;
  let t45;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = {
      base: 4,
      md: 6,
      lg: 8
    };
    $[101] = t45;
  } else {
    t45 = $[101];
  }
  let t46;
  if ($[102] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = {
      base: "block",
      md: "none"
    };
    $[102] = t46;
  } else {
    t46 = $[102];
  }
  let t47;
  if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <Box display={t46} paddingTop={1}><AppPanelToggleButton panelId="advisors-directory-nav" /></Box>;
    $[103] = t47;
  } else {
    t47 = $[103];
  }
  const t48 = isMobile ? "xl" : "3xl";
  let t49;
  if ($[104] !== t48) {
    t49 = <Heading size={t48}>Find advisors</Heading>;
    $[104] = t48;
    $[105] = t49;
  } else {
    t49 = $[105];
  }
  const t50 = isMobile ? "sm" : "md";
  let t51;
  if ($[106] !== t50) {
    t51 = <Text color="colorPalette.fg/60" fontSize={t50}>Approved marketplace advisors for your board or paid sessions.</Text>;
    $[106] = t50;
    $[107] = t51;
  } else {
    t51 = $[107];
  }
  let t52;
  if ($[108] !== t49 || $[109] !== t51) {
    t52 = <HStack align="start" flex={1} gap={2} minWidth={0}>{t47}<VStack align="start" gap={0.5} minWidth={0}>{t49}{t51}</VStack></HStack>;
    $[108] = t49;
    $[109] = t51;
    $[110] = t52;
  } else {
    t52 = $[110];
  }
  const t53 = !initialLoad && itemCount > 0 ? ` · ${itemCount}` : "";
  let t54;
  if ($[111] !== resultsLabel || $[112] !== t53) {
    t54 = <Text color="colorPalette.fg/60" textStyle="labelXs" whiteSpace="nowrap">{resultsLabel}{t53}</Text>;
    $[111] = resultsLabel;
    $[112] = t53;
    $[113] = t54;
  } else {
    t54 = $[113];
  }
  let t55;
  if ($[114] !== t52 || $[115] !== t54) {
    t55 = <HStack align="end" gap={3} justify="space-between" wrap="wrap">{t52}{t54}</HStack>;
    $[114] = t52;
    $[115] = t54;
    $[116] = t55;
  } else {
    t55 = $[116];
  }
  let t56;
  if ($[117] !== initialLoad) {
    t56 = initialLoad && <Center flex={1} minHeight="12rem" padding={8}><Spinner /></Center>;
    $[117] = initialLoad;
    $[118] = t56;
  } else {
    t56 = $[118];
  }
  let t57;
  if ($[119] !== query.error) {
    t57 = query.error && <Text color="red.fg" fontSize="sm">{query.error.message}</Text>;
    $[119] = query.error;
    $[120] = t57;
  } else {
    t57 = $[120];
  }
  let t58;
  if ($[121] !== advisors || $[122] !== clearAllFilters || $[123] !== hasActiveFilters || $[124] !== initialLoad || $[125] !== paths.browse) {
    t58 = !initialLoad && advisors.length === 0 && <Center padding={8}><VStack gap={3} textAlign="center"><Heading size="md">No advisors found</Heading><Text color="colorPalette.fg/60" fontSize="sm">Try a different search or pick another topic.</Text><HStack gap={2} justify="center" wrap="wrap">{hasActiveFilters && <Button size="sm" variant="outline" onClick={clearAllFilters}>Clear filters</Button>}<Button asChild={true} size="sm" variant="ghost"><NextLink href={paths.browse}>Browse topics</NextLink></Button></HStack></VStack></Center>;
    $[121] = advisors;
    $[122] = clearAllFilters;
    $[123] = hasActiveFilters;
    $[124] = initialLoad;
    $[125] = paths.browse;
    $[126] = t58;
  } else {
    t58 = $[126];
  }
  let t59;
  if ($[127] !== advisors || $[128] !== currentUser?.userId || $[129] !== hasOpenSeats || $[130] !== initialLoad || $[131] !== namedSeatMatches || $[132] !== paths || $[133] !== router) {
    t59 = !initialLoad && advisors.length > 0 && <SimpleGrid columns={{
      base: 1,
      sm: 2,
      xl: 3
    }} gap={4}>{advisors.map(advisor => {
        const user = advisor.user;
        const skillLabels = (advisor.categories ?? []).filter(_temp2);
        const slug = user.slug || user.userId;
        const namedMatch = user.userId ? namedSeatMatches.get(user.userId) : undefined;
        const profileHref = `${paths.profile}/${slug}`;
        return <Card.Root key={advisor.advisorProfileId} overflow="hidden" variant="interactive" onClick={() => router.push(profileHref)}><Card.Body alignItems="center" gap={4} textAlign="center"><Avatar.Root size="xl">{user.pictureUrl ? <Avatar.Image src={user.pictureUrl} /> : null}<Avatar.Fallback name={user.fullName || user.displayName || "Advisor"} /></Avatar.Root><VStack gap={1}><Text fontSize="lg" fontWeight="semibold">{user.fullName || user.displayName}</Text><Text color="colorPalette.fg/80" fontSize="sm" fontWeight="medium" lineClamp={3}>{user.shortBio || advisor.headline || user.title}</Text>{(advisor.bioForAdvising || advisor.advisingNote) && <Text color="colorPalette.fg/55" fontSize="xs" lineClamp={2}>{advisor.bioForAdvising || advisor.advisingNote}</Text>}</VStack>{namedMatch && <Badge colorPalette="green" title={namedMatch.badgeLabel} variant="subtle">{namedMatch.badgeLabel}</Badge>}<Wrap gap={1} justify="center">{skillLabels.slice(0, 4).map(_temp3)}</Wrap><VStack gap={2} width="full">{advisorMarketplaceActions({
                canAcceptSessions: advisor.canAcceptSessions,
                hasOpenSeats,
                isSelf: !!user.userId && user.userId === currentUser?.userId
              }).map(action => user.userId ? <AdvisorMarketplaceActionButton key={action.key} action={action} paths={paths} slug={slug} userId={user.userId} onInvite={() => setInviteUserId(user.userId)} /> : null)}</VStack></Card.Body></Card.Root>;
      })}</SimpleGrid>;
    $[127] = advisors;
    $[128] = currentUser?.userId;
    $[129] = hasOpenSeats;
    $[130] = initialLoad;
    $[131] = namedSeatMatches;
    $[132] = paths;
    $[133] = router;
    $[134] = t59;
  } else {
    t59 = $[134];
  }
  let t60;
  if ($[135] !== hasNextPage || $[136] !== loadMore || $[137] !== loadingMore) {
    t60 = <AdvisorsLoadMore hasNextPage={hasNextPage} loading={loadingMore} onLoadMore={loadMore} />;
    $[135] = hasNextPage;
    $[136] = loadMore;
    $[137] = loadingMore;
    $[138] = t60;
  } else {
    t60 = $[138];
  }
  let t61;
  if ($[139] !== t55 || $[140] !== t56 || $[141] !== t57 || $[142] !== t58 || $[143] !== t59 || $[144] !== t60) {
    t61 = <VStack align="stretch" gap={4} marginX="auto" maxWidth="6xl" padding={t45} width="full">{t55}{t56}{t57}{t58}{t59}{t60}</VStack>;
    $[139] = t55;
    $[140] = t56;
    $[141] = t57;
    $[142] = t58;
    $[143] = t59;
    $[144] = t60;
    $[145] = t61;
  } else {
    t61 = $[145];
  }
  const resultsPanel = t61;
  let t62;
  if ($[146] === Symbol.for("react.memo_cache_sentinel")) {
    t62 = {
      base: true,
      md: false
    };
    $[146] = t62;
  } else {
    t62 = $[146];
  }
  let t63;
  if ($[147] === Symbol.for("react.memo_cache_sentinel")) {
    t63 = {
      base: 4,
      md: 5
    };
    $[147] = t63;
  } else {
    t63 = $[147];
  }
  let t64;
  if ($[148] !== filterPanel) {
    t64 = {
      id: "advisors-directory-nav",
      defaultWidth: 280,
      minWidth: 220,
      maxWidth: 360,
      isResizable: true,
      resizeHandlePosition: "right",
      useDrawerBreakpoints: t62,
      containerProps: attachedNavPanelContainerProps,
      children: <Box height="full" minHeight={0} overflowY="auto" padding={t63} width="full">{filterPanel}</Box>
    };
    $[148] = filterPanel;
    $[149] = t64;
  } else {
    t64 = $[149];
  }
  let t65;
  if ($[150] !== resultsPanel) {
    t65 = {
      id: "advisors-directory-main",
      containerProps: attachedPanelContainerProps,
      children: <Box flex={1} height="full" minHeight={0} minWidth={0} overflowY="auto" width="full">{resultsPanel}</Box>
    };
    $[150] = resultsPanel;
    $[151] = t65;
  } else {
    t65 = $[151];
  }
  let t66;
  if ($[152] !== t64 || $[153] !== t65) {
    t66 = <NestedAppLayout id="advisors-directory" panels={[t64, t65]} />;
    $[152] = t64;
    $[153] = t65;
    $[154] = t66;
  } else {
    t66 = $[154];
  }
  let t67;
  if ($[155] !== inviteUserId || $[156] !== refetchSeatMatches) {
    t67 = inviteUserId && <InviteToBoardModal advisorUserId={inviteUserId} isOpen={!!inviteUserId} onClose={() => setInviteUserId(null)} onSuccess={() => {
      refetchSeatMatches();
    }} />;
    $[155] = inviteUserId;
    $[156] = refetchSeatMatches;
    $[157] = t67;
  } else {
    t67 = $[157];
  }
  let t68;
  if ($[158] !== t66 || $[159] !== t67) {
    t68 = <>{t66}{t67}</>;
    $[158] = t66;
    $[159] = t67;
    $[160] = t68;
  } else {
    t68 = $[160];
  }
  return t68;
};
function _temp(item) {
  return !!item?.user?.userId;
}
function _temp2(category_0) {
  return Boolean(category_0?.title);
}
function _temp3(category_1) {
  return <SpecialtyTag key={category_1.key ?? category_1.title} label={category_1.title} />;
}